import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Open/close + Esc handling for the global ⌘K palette. */
export function useCmdKHotkey() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return [open, setOpen];
}

/** Debounced admin search hook. Returns {results, loading}. */
export function useAdminSearch(query, enabled) {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    if (!query.trim()) { setResults({}); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/search", { params: { q: query } });
        if (!cancelled) setResults(r.data);
      } catch (err) {
        console.warn("Admin search failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, enabled]);
  return { results, loading };
}

/** Arrow-key navigation over a flat list of selectable rows. */
export function useArrowNav(open, length, onSelect) {
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => { setActiveIdx(0); }, [length]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
      else if (e.key === "Enter") { e.preventDefault(); onSelect(activeIdx); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, length, activeIdx, onSelect]);
  return [activeIdx, setActiveIdx];
}
